import React, { useState } from 'react';
import { format } from 'date-fns';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from './ui/command';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Check, ChevronsUpDown, Plus, PackagePlus } from 'lucide-react';
import { formatUGX } from '../mock';
import DatePickerField from './DatePickerField';

const SupplierCombobox = ({ suppliers, value, onChange, onAddNew }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const hasExact = suppliers.some((s) => s.toLowerCase() === search.trim().toLowerCase());

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox"
          className={`mt-1.5 h-11 w-full justify-between font-normal ${!value ? 'text-slate-400' : 'text-slate-800'}`}>
          {value || 'Select supplier'}
          <ChevronsUpDown size={15} className="ml-2 text-slate-400 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
        <Command>
          <CommandInput placeholder="Search supplier..." value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty>
              <span className="px-2 text-slate-400 text-sm">No matching supplier</span>
            </CommandEmpty>
            <CommandGroup>
              {suppliers.map((s) => (
                <CommandItem key={s} value={s} onSelect={() => { onChange(s); setOpen(false); }}>
                  <Check size={15} className={`mr-2 ${value === s ? 'opacity-100 text-blue-600' : 'opacity-0'}`} />
                  {s}
                </CommandItem>
              ))}
            </CommandGroup>
            {search.trim() && !hasExact && (
              <div className="border-t border-slate-100 p-1">
                <button className="w-full flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-md"
                  onClick={() => { onAddNew(search.trim()); onChange(search.trim()); setSearch(''); setOpen(false); }}>
                  <Plus size={15} /> Add new supplier "{search.trim()}"
                </button>
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const RestockDialog = ({ product, open, onOpenChange, suppliers, onAddSupplier, onSubmit }) => {
  const emptyForm = { qty: '', unitCost: '', batchNo: '', mfgDate: undefined, expDate: undefined, supplier: '', sellingPrice: '' };
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});

  // Reset form whenever a new product is opened
  React.useEffect(() => {
    if (product) setForm({ ...emptyForm, sellingPrice: String(product.price) });
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.qty || Number(form.qty) <= 0) e.qty = 'Enter quantity received';
    if (!form.unitCost || Number(form.unitCost) <= 0) e.unitCost = 'Enter unit cost price';
    if (!form.batchNo.trim()) e.batchNo = 'Batch number required';
    if (!form.mfgDate) e.mfgDate = 'Select manufacture date';
    if (!form.expDate) e.expDate = 'Select expiry date';
    if (form.mfgDate && form.expDate && form.expDate <= form.mfgDate) e.expDate = 'Expiry must be after manufacture date';
    if (!form.supplier) e.supplier = 'Select or add a supplier';
    if (!form.sellingPrice || Number(form.sellingPrice) <= 0) e.sellingPrice = 'Enter selling price';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = () => {
    if (!validate()) return;
    onSubmit({
      qty: Number(form.qty),
      unitCost: Number(form.unitCost),
      batchNo: form.batchNo.trim(),
      mfgDate: format(form.mfgDate, 'yyyy-MM-dd'),
      expDate: format(form.expDate, 'yyyy-MM-dd'),
      supplier: form.supplier,
      sellingPrice: Number(form.sellingPrice),
    });
  };

  const Err = ({ name }) => errors[name] ? <p className="text-red-500 text-xs mt-1">{errors[name]}</p> : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <PackagePlus size={20} className="text-blue-600" /> Restock: {product?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-700">Quantity received</Label>
              <Input type="number" value={form.qty} onChange={(e) => set('qty', e.target.value)} placeholder="e.g. 200" className="mt-1.5 h-11" />
              <Err name="qty" />
            </div>
            <div>
              <Label className="text-slate-700">Unit cost price (UGX)</Label>
              <Input type="number" value={form.unitCost} onChange={(e) => set('unitCost', e.target.value)} placeholder="Cost paid per unit" className="mt-1.5 h-11" />
              <Err name="unitCost" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-700">Lot / Batch number</Label>
              <Input value={form.batchNo} onChange={(e) => set('batchNo', e.target.value)} placeholder="e.g. AMX-2508" className="mt-1.5 h-11" />
              <Err name="batchNo" />
            </div>
            <div>
              <Label className="text-slate-700">Selling price (UGX)</Label>
              <Input type="number" value={form.sellingPrice} onChange={(e) => set('sellingPrice', e.target.value)} className="mt-1.5 h-11" />
              <p className="text-slate-400 text-xs mt-1">Current: {product ? formatUGX(product.price) : '—'} · editable</p>
              <Err name="sellingPrice" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-700">Manufacture date</Label>
              <DatePickerField value={form.mfgDate} onChange={(d) => set('mfgDate', d)} placeholder="DD/MM/YYYY" />
              <Err name="mfgDate" />
            </div>
            <div>
              <Label className="text-slate-700">Expiry date</Label>
              <DatePickerField value={form.expDate} onChange={(d) => set('expDate', d)} placeholder="DD/MM/YYYY" warnIfPast />
              <Err name="expDate" />
            </div>
          </div>

          <div>
            <Label className="text-slate-700">Supplier / Vendor</Label>
            <SupplierCombobox suppliers={suppliers} value={form.supplier}
              onChange={(v) => set('supplier', v)} onAddNew={onAddSupplier} />
            <Err name="supplier" />
          </div>

          {form.qty && form.unitCost && (
            <div className="bg-slate-50 rounded-xl px-4 py-3 flex items-center justify-between">
              <span className="text-slate-500 text-sm">Total batch cost</span>
              <span className="font-display font-bold text-slate-900">{formatUGX(Number(form.qty) * Number(form.unitCost))}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-blue-600 hover:bg-blue-700 text-white gap-2"><PackagePlus size={16} /> Add stock</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RestockDialog;
